import { Link, useLocation } from "react-router-dom";
import { Bell, MessageCircle, Wallet, Radio, Target, Search, ArrowLeft, Maximize2, Minimize2 } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";

export default function TopBar({ title }) {
  const [unread, setUnread] = useState(0);
  const [followedLive, setFollowedLive] = useState(0); // count of followed creators currently live
  const [isFs, setIsFs] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/" || pathname === "";

  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await (document.documentElement.requestFullscreen?.() ||
               document.documentElement.webkitRequestFullscreen?.());
      } else {
        await (document.exitFullscreen?.() || document.webkitExitFullscreen?.());
      }
    } catch { /* browser may block if not user-initiated */ }
  };

  useEffect(() => {
    const loadNotif = () => api.get("/notifications").then(r => setUnread(r.data.unread_count || 0)).catch(()=>{});
    const loadFollowedLive = async () => {
      try {
        const [f, live] = await Promise.all([
          api.get("/me/following-ids"),
          api.get("/live/active"),
        ]);
        const followed = new Set(f.data?.ids || []);
        const count = (live.data || []).filter(s => followed.has(s.user_id || s.user?.id)).length;
        setFollowedLive(count);
      } catch { /* logged out or endpoint missing */ }
    };
    loadNotif();
    loadFollowedLive();
    const t1 = setInterval(loadNotif, 30000);
    const t2 = setInterval(loadFollowedLive, 30000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  return (
    <div data-testid="top-bar" className="fixed top-0 inset-x-0 z-40 max-w-md md:max-w-none mx-auto">
      <div className="glass border-b border-white/10 px-3 py-3 flex items-center justify-between md:max-w-6xl md:mx-auto">
        <div className="flex items-center gap-1.5">
          {!isHome && (
            <Link to="/" data-testid="top-back-home"
              className="p-2 -ml-1 rounded-full hover:bg-white/10 active:scale-90 transition"
              aria-label="Back to home">
              <ArrowLeft className="w-4 h-4 text-white/80" />
            </Link>
          )}
          <Link to="/" className="flex items-center gap-2" data-testid="top-logo">
            <img src="/logo192.png" alt="Hifireels" className="w-9 h-9 rounded-xl object-cover shadow-[0_0_12px_rgba(255,42,95,0.35)]"/>
            <span className="font-outfit font-extrabold text-base tracking-tight bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00] bg-clip-text text-transparent">{title || "HIFIREELS"}</span>
          </Link>
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={toggleFullscreen} data-testid="top-fullscreen"
            className="p-2 rounded-full hover:bg-white/10 active:scale-90 transition"
            aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"} title={isFs ? "Exit fullscreen" : "Fullscreen"}>
            {isFs
              ? <Minimize2 className="w-4 h-4 text-[#FF7A00]"/>
              : <Maximize2 className="w-4 h-4 text-[#FF7A00]"/>}
          </button>
          <Link to="/search" data-testid="top-search" className="p-2 rounded-full hover:bg-white/10">
            <Search className="w-4 h-4 text-white/80" />
          </Link>
          <Link to="/live" data-testid="live-btn" className="relative flex items-center gap-1 px-2 py-1.5 rounded-full bg-[#FF2A5F]/15 border border-[#FF2A5F]/30 text-[#FF2A5F] text-[10px] font-bold uppercase tracking-wide">
            <Radio className="w-3 h-3 animate-pulse" />Live
            {followedLive > 0 && (
              <span data-testid="followed-live-dot"
                className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF2A5F] text-white text-[9px] font-extrabold flex items-center justify-center ring-2 ring-black animate-pulse"
                title={`${followedLive} creator${followedLive > 1 ? 's' : ''} you follow ${followedLive > 1 ? 'are' : 'is'} live now`}>
                {followedLive > 9 ? "9+" : followedLive}
              </span>
            )}
          </Link>
          <Link to="/missions" data-testid="top-missions" className="p-2 rounded-full hover:bg-white/10">
            <Target className="w-4 h-4 text-[#00E5FF]" />
          </Link>
          <Link to="/wallet" data-testid="top-wallet" className="p-2 rounded-full hover:bg-white/10">
            <Wallet className="w-4 h-4 text-[#FF7A00]" />
          </Link>
          <Link to="/notifications" data-testid="top-notif" className="p-2 rounded-full hover:bg-white/10 relative">
            <Bell className="w-4 h-4" />
            {unread > 0 && <span className="absolute top-0 right-0 min-w-[14px] h-3.5 px-1 rounded-full bg-[#FF2A5F] text-[8px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
          </Link>
          <Link to="/dm" data-testid="top-dm" className="p-2 rounded-full hover:bg-white/10">
            <MessageCircle className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
